import { PAYMENT_METHODS_PIBOX } from 'utils/constants';

export const STOP_STATUS_COLOR = {
  pending: '#9A68C2',
  in_progress: '#9A68C2',
  completed: '#93ca00',
  canceled: '#B0B0B0',
};

export const stopStatusColor = (status) => STOP_STATUS_COLOR[status] || STOP_STATUS_COLOR.pending;

const ACTION_LABELS = {
  pickup: 'app.deliveryRoute.action.pickup',
  dropoff: 'app.deliveryRoute.action.dropoff',
  return: 'app.deliveryRoute.action.return',
  mixed: 'app.deliveryRoute.action.mixed',
};

export const stopActionLabel = (action, translate) => {
  const key = ACTION_LABELS[action] || ACTION_LABELS.mixed;
  return translate(key);
};

const PACKAGE_STATUS_LABELS = {
  waiting_for_pick_up: 'app.deliveryRoute.packageStatus.waitingForPickUp',
  picked_up: 'app.deliveryRoute.packageStatus.pickedUp',
  delivered: 'app.deliveryRoute.packageStatus.delivered',
  returned: 'app.deliveryRoute.packageStatus.returned',
  cancelled: 'app.deliveryRoute.packageStatus.cancelled',
  not_received: 'app.deliveryRoute.packageStatus.notReceived',
  not_visited: 'app.deliveryRoute.packageStatus.notVisited',
};

export const packageStatusLabel = (status, translate) => {
  const key = PACKAGE_STATUS_LABELS[status];
  return key ? translate(key) : status;
};

// Statuses in which the route no longer changes, so the detail page can stop polling.
const TERMINAL_ROUTE_STATUSES = [8, 9, 10, 11]; // finalized_route, canceled, finished_by_ops, expired

export const routeIsTerminal = (statusCd) => TERMINAL_ROUTE_STATUSES.indexOf(statusCd) !== -1;

// Matches Pibox::DeliveryRoute::STATUS_OPTIONS (picap_api). Every non-terminal status reads
// as "in progress" for the header badge — the tab-level detail (waiting_on_stop, in_transit,
// etc.) isn't worth surfacing there.
const ROUTE_STATUS_BADGE = {
  9: { color: '#B0B0B0', label: 'app.deliveryRoute.routeStatus.canceled' }, // canceled
  8: { color: '#93ca00', label: 'app.deliveryRoute.routeStatus.finished' }, // finalized_route
  10: { color: '#93ca00', label: 'app.deliveryRoute.routeStatus.finished' }, // finished_by_ops
  11: { color: '#B0B0B0', label: 'app.deliveryRoute.routeStatus.expired' }, // expired
};

export const routeStatusBadge = (statusCd, translate) => {
  const badge = ROUTE_STATUS_BADGE[statusCd] || { color: '#9A68C2', label: 'app.deliveryRoute.routeStatus.inProgress' };
  return { color: badge.color, label: translate(badge.label) };
};

// Per-company reading of the route, derived by the backend from the viewer's own packages
// (Pibox::DeliveryRoutes::CompanyStatus). Only present when a company_id filter is active
// and the route is neither terminal nor relaunched; otherwise the header falls back to
// routeStatusBadge above, so every viewer of a finished route reads the same thing.
const COMPANY_STATUS_BADGE = {
  scheduled: { color: '#9A68C2', label: 'app.deliveryRoute.companyStatus.scheduled' },
  going_to_pickup: { color: '#9A68C2', label: 'app.deliveryRoute.companyStatus.goingToPickup' },
  picking_up_others: { color: '#9A68C2', label: 'app.deliveryRoute.companyStatus.pickingUpOthers' },
  going_to_dropoff: { color: '#9A68C2', label: 'app.deliveryRoute.companyStatus.goingToDropoff' },
  delivering_others: { color: '#9A68C2', label: 'app.deliveryRoute.companyStatus.deliveringOthers' },
  completed: { color: '#93ca00', label: 'app.deliveryRoute.companyStatus.completed' },
  closed_with_incidents: { color: '#B0B0B0', label: 'app.deliveryRoute.companyStatus.closedWithIncidents' },
};

// {count} is substituted by hand because translate() in this repo has no interpolation,
// same as stopsAwayLabel below. An unknown status shows raw rather than disappearing.
export const companyStatusBadge = (companyStatus, translate) => {
  if (!companyStatus) return null;

  const badge = COMPANY_STATUS_BADGE[companyStatus.status];
  if (!badge) return { color: '#9A68C2', label: companyStatus.status };

  return {
    color: badge.color,
    label: translate(badge.label).replace('{count}', companyStatus.stops_away),
  };
};

// translate() in this repo has no interpolation, so {count} is substituted by hand — same
// pattern as stopsAwayLabel below.
export const packagesCountLabel = (count, translate) => {
  const key = count === 1 ? 'app.deliveryRoute.packageCountOne' : 'app.deliveryRoute.packageCountMany';
  return translate(key).replace('{count}', count);
};

// pkg.payment.method_cd only resolves the PrePackage cross-reference; when it doesn't
// resolve, fall back to the raw method string the backend already sends, then '-'.
export const paymentMethodLabel = (payment, translate) => {
  const key = PAYMENT_METHODS_PIBOX[payment?.method_cd];
  if (key) return translate(key);
  return payment?.method || '-';
};

// stop.stops_away counts stops of the FULL route (including ones from other companies,
// never shown to this viewer) between the driver and this stop. translate() in this repo
// has no interpolation (key => string), so {count} is substituted by hand.
export const stopsAwayLabel = (stop, translate) => {
  if (stop.status === 'completed') return translate('app.deliveryRoute.stopCompleted');
  if (stop.stops_away === null || stop.stops_away === undefined) return null;
  if (stop.stops_away === 0) return translate('app.deliveryRoute.youAreNextStop');

  const key = stop.stops_away === 1
    ? 'app.deliveryRoute.stopsAwayOne'
    : 'app.deliveryRoute.stopsAwayMany';
  return translate(key).replace('{count}', stop.stops_away);
};
